import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowRightCircleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  Bars3Icon,
  BookOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import {
  Badge,
  Button,
  PlaceholderEditor,
  PlaceholderEditorVariant,
  Small,
  TableBuilderDynamic,
  WYSIWYG,
} from "@fronterahealth/frontera-ui-components";

import {
  type Curriculum,
  type GetPromptsOutput,
  type OrganizationSummary,
  type PromptSummary,
  RegistryStatusEnum,
  type ReportTypeEnum,
  type Section,
  type TreatmentPlanTemplateData,
  updateTemplate,
  useCopyField,
  useCopySection,
} from "@api/rest/types-and-hooks";
import { ReportTypeChips } from "@components/Prompts/ReportTypeTags";
import { SampleOutputPreview } from "@components/Prompts/SampleOutputPreview";
import { GLOBAL_ORG_SENTINEL, REPORT_TYPE_OPTIONS } from "@components/Prompts/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { FieldActionMenu } from "@pages/Templates/FieldActionMenu";
import { ProgrammingTab } from "@pages/Templates/ProgrammingTab";
import { PromptReportTypeBadge } from "@pages/Templates/PromptReportTypeBadge";
import { SectionActionMenu } from "@pages/Templates/SectionActionMenu";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";
import { Tip } from "@pages/Templates/Tip";
import {
  ClipboardItemType,
  ExportFramework,
  FIELD_TYPES_WITH_OPTIONS,
  FieldType,
  REPORT_TYPE_LABELS,
  ReportType,
  type ReportTypeValue,
  StructureSubTab,
  type StructureSubTabValue,
  TABLE_FIELD_TYPES,
  WET_SIGNATURE_METHODS,
  WET_SIGNATURE_METHOD_LABELS,
  type WetSignatureMethodValue,
} from "@pages/Templates/constants";
import { useAllPrompts } from "@pages/Templates/hooks/useAllPrompts";
import {
  EDITOR_CONTENT_INSET_CLASS,
  PlaceholderValues,
  convertDisplayPlaceholdersToKeys,
  convertKeyPlaceholdersToDisplay,
  htmlToMarkdownIfNeeded,
} from "@pages/Templates/placeholders";
import { useTemplateClipboard } from "@pages/Templates/templateClipboard";

// --- Structure Tab ---

interface PromptDropdownProps {
  value: PromptSummary | null | undefined;
  options: GetPromptsOutput[];
  onChange: (selected: GetPromptsOutput | null) => void;
  /** The template's report type; the picker defaults to prompts tagged for it. */
  reportType: string;
}

const PromptDropdown: FC<PromptDropdownProps> = ({ value, options, onChange, reportType }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  // "" = all report types. Untagged prompts only appear under "All".
  const [reportTypeFilter, setReportTypeFilter] = useState(reportType);
  const ref = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Distinct org owners across the picker's option pool. Sorted by name
  // so the dropdown order is stable across renders.
  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of options) {
      if (p.organization) seen.set(p.organization.id, p.organization.name);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [options]);

  const q = search.trim().toLowerCase();
  const filtered = options.filter((o) => {
    if (reportTypeFilter && !o.report_types.includes(reportTypeFilter as ReportTypeEnum)) return false;
    if (orgFilter === GLOBAL_ORG_SENTINEL && o.organization) return false;
    if (orgFilter && orgFilter !== GLOBAL_ORG_SENTINEL && o.organization?.id !== orgFilter) {
      return false;
    }
    if (!q) return true;
    return (
      o.name.toLowerCase().includes(q) ||
      o.key.toLowerCase().includes(q) ||
      (o.organization?.name ?? "").toLowerCase().includes(q)
    );
  });

  const label = value ? `${value.name} (${value.key})` : "No AI prompt";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className="flex w-full items-center justify-between rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary hover:bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-500"
      >
        <span className={`truncate ${value ? "text-text-primary" : "text-text-tertiary"}`}>{label}</span>
        <span className="ml-2 text-text-tertiary">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-96 rounded-md border border-border-subtle bg-bg-surface shadow-md">
          <div className="sticky top-0 flex gap-2 border-b border-border-subtle bg-bg-surface p-2">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, key, or org…"
              className="flex-1 rounded border border-border-medium px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            />
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              className="rounded border border-border-medium bg-bg-surface px-2 py-1 pr-6 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            >
              <option value="">All orgs</option>
              <option value={GLOBAL_ORG_SENTINEL}>Global</option>
              {orgOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <select
              value={reportTypeFilter}
              onChange={(e) => setReportTypeFilter(e.target.value)}
              className="rounded border border-border-medium bg-bg-surface px-2 py-1 pr-6 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            >
              <option value="">All report types</option>
              {REPORT_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {REPORT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                close();
              }}
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-bg-default ${
                value ? "text-text-secondary" : "bg-bg-default text-text-primary"
              }`}
            >
              No AI prompt
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-text-tertiary">
                {reportTypeFilter
                  ? `No ${REPORT_TYPE_LABELS[reportTypeFilter as ReportTypeValue] ?? reportTypeFilter} prompts match — try "All report types".`
                  : "No matches"}
              </div>
            ) : (
              filtered.map((p) => {
                const schemaKeys = getSchemaPropertyKeys(p.response_schema);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => {
                      onChange(p);
                      close();
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-default ${
                      value?.id === p.id ? "bg-bg-default text-text-primary" : "text-text-secondary"
                    }`}
                  >
                    <span className="truncate min-w-0 flex-1">
                      {p.name} <span className="text-text-tertiary">({p.key})</span>
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${
                        p.organization ? "bg-blue-100 text-blue-700" : "bg-limestone-100 text-text-tertiary italic"
                      }`}
                    >
                      {p.organization?.name ?? "Global"}
                    </span>
                    <ReportTypeChips reportTypes={p.report_types} />
                    {schemaKeys && (
                      <span
                        className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 shrink-0"
                        title={`AI outputs: ${schemaKeys.join(", ")}`}
                      >
                        {schemaKeys.length} {schemaKeys.length === 1 ? "output" : "outputs"}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function parseTableContent(content: string | null): { headers: string[]; data: object[] } {
  if (!content) return { headers: ["Column 1", "Column 2"], data: [] };
  try {
    const parsed = JSON.parse(content) as { headers?: string[]; data?: object[] };
    return { headers: parsed.headers ?? ["Column 1", "Column 2"], data: parsed.data ?? [] };
  } catch {
    return { headers: ["Column 1", "Column 2"], data: [] };
  }
}

// --- Slug helpers for key/field_identifier auto-generation ---

function toSlug(title: string): string {
  return (
    title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "field"
  );
}

function uniqueSlug(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let i = 2;
  while (taken.includes(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

// Lightweight hover tooltip wrapper — no info icon, instant on hover
function formatKeyForFramework(
  key: string,
  sectionKey: string | undefined,
  exportFramework: string | undefined,
): string {
  if (!sectionKey) return key;
  if (exportFramework === ExportFramework.DOCXTEMPLATER) return `${sectionKey}_${key}`;
  if (exportFramework === ExportFramework.GOOGLE_DOCS) return `${sectionKey}.${key}`;
  return key;
}

function EditableKeyBadge({
  value,
  label,
  takenKeys,
  onSave,
  exportFramework,
  sectionKey,
}: {
  value: string;
  label?: string;
  takenKeys: string[];
  onSave: (v: string) => void;
  exportFramework?: string;
  sectionKey?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDuplicate = takenKeys.includes(draft.trim()) && draft.trim() !== value;

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed || isDuplicate) {
      setDraft(value);
      setEditing(false);
      return;
    }
    onSave(trimmed);
    setEditing(false);
  };

  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const copyValue = formatKeyForFramework(value, sectionKey, exportFramework);
    void navigator.clipboard.writeText(copyValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (editing) {
    return (
      <div className="inline-flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(value);
              setEditing(false);
            }
          }}
          className={`font-mono text-xs rounded border px-1.5 py-0.5 outline-none focus:ring-1 ${
            isDuplicate
              ? "border-red-400 focus:ring-red-300 bg-red-50 text-text-error-primary"
              : "border-limestone-400 focus:ring-limestone-400 bg-bg-default"
          }`}
        />
        {isDuplicate && <span className="text-[10px] text-text-error-primary">Key already exists</span>}
      </div>
    );
  }

  return (
    <Tip text={copied ? "Copied!" : `Click to edit ${label ?? "key"} · click icon to copy`}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setDraft(value);
          setEditing(true);
        }}
        className="inline-flex items-center gap-1 rounded font-mono bg-limestone-50 px-1.5 py-0.5 text-xs text-text-tertiary hover:bg-limestone-100 group max-w-[160px]"
      >
        <span className="truncate">{value}</span>
        <ClipboardDocumentIcon
          className="h-3 w-3 shrink-0 text-text-tertiary group-hover:text-text-secondary"
          onClick={copy}
        />
      </button>
    </Tip>
  );
}

const PAGE_LOCATION_LABELS: Record<string, string> = {
  "report-part-1": "Report Part 1",
  "report-part-2": "Report Part 2",
  conclusions: "Conclusions",
  recommendations: "Recommendations",
  "treatment-plan": "Treatment Plan",
  appendix: "Appendix",
};

// Matches portal's getPageLocations logic (actual DB values are lowercase-hyphenated)
function getPageLocations(reportType: string): string[] | null {
  if (reportType === ReportType.INITIAL_ASSESSMENT) {
    return ["report-part-1", "report-part-2"];
  }
  if (reportType === ReportType.REASSESSMENT) {
    return ["report-part-1"];
  }
  if (reportType === "DIAGNOSIS_EVALUATION") {
    return ["report-part-1", "report-part-2", "conclusions", "recommendations"];
  }
  // DATA_BASED_MODIFICATION and others are single-page — no location dividers
  return null;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  SIMPLE_TEXT: "Simple Text",
  WYSIWYG: "Formatted Text",
  SELECT: "Dropdown",
  CHECKBOX: "CheckBoxes",
  RADIO: "Radio Button",
  DATE: "Date Picker",
  DATETIME: "Date Time Picker",
  IMAGE: "Image",
  ASSESSMENT_IMAGE: "Assessment Image",
  BLANK_SIGNATURE_LINE: "Signature Line",
  TYPED_SIGNATURE: "Digital Signature",
  WET_SIGNATURE: "Wet Signature",
  TREATMENT_PLAN: "Goals",
  TABLE: "Table",
  ASSESSMENT_APPOINTMENTS: "Assessment Appointments",
  DSM_TABLE: "DSM Table",
  COORDINATION_OF_CARE: "Coordination of Care",
  TREATMENT_OPTION_TABLE: "Treatment Options",
};

// Local mutable representation used for edits
interface LocalField {
  localId: string;
  id?: number;
  title: string;
  rank_order: number;
  field_type: string;
  is_required: boolean;
  is_auditable: boolean;
  field_identifier: string | null;
  identifierLocked: boolean;
  prompt: PromptSummary | null;
  sample_data: string | null;
  content: string | null;
  input_dependency: string[] | null;
  table_key: string | null;
}

interface LocalSection {
  localId: string;
  id?: number;
  title: string;
  rank_order: number;
  key: string | null;
  keyLocked: boolean;
  page_location: string;
  input_dependency: string[] | null;
  fields: LocalField[];
}

function remapPageLocation(pageLocation: string, reportType: string): string {
  const validLocations = getPageLocations(reportType);
  if (validLocations && !validLocations.includes(pageLocation)) {
    return validLocations[0];
  }
  return pageLocation;
}

function toLocal(sections: readonly Section[], reportType?: string): LocalSection[] {
  // Determine canonical page order so sections are grouped by page (never interleaved).
  // Templates created by portal use per-page rank_orders (both pages start at 1, 2, 3…),
  // which causes interleaving when sorted globally. Grouping by page first ensures that
  // drag-and-drop indices within a page are always contiguous.
  const pageOrder = reportType ? (getPageLocations(reportType) ?? []) : [];

  const mapped = [...sections].map((s) => {
    const newLoc = reportType ? remapPageLocation(s.page_location, reportType) : s.page_location;
    return {
      localId: String(s.id),
      id: s.id,
      title: s.title,
      rank_order: s.rank_order,
      key: s.key,
      keyLocked: true,
      page_location: newLoc,
      _originalLoc: s.page_location,
      input_dependency: (s.input_dependency as string[] | null) ?? null,
      fields: [...s.section_fields]
        .sort((a, b) => a.rank_order - b.rank_order)
        .map((f) => ({
          localId: String(f.id),
          id: f.id,
          title: f.title,
          rank_order: f.rank_order,
          field_type: f.field_type,
          is_required: f.is_required,
          is_auditable: f.is_auditable,
          field_identifier: f.field_identifier,
          identifierLocked: true,
          prompt: f.prompt,
          sample_data: f.sample_data,
          content:
            (TABLE_FIELD_TYPES as readonly string[]).includes(f.field_type) ||
            (FIELD_TYPES_WITH_OPTIONS as readonly string[]).includes(f.field_type)
              ? convertKeyPlaceholdersToDisplay(f.content ?? "") || null
              : htmlToMarkdownIfNeeded(convertKeyPlaceholdersToDisplay(f.content ?? "")) || null,
          input_dependency: (f.input_dependency as string[] | null) ?? null,
          table_key: f.table_key,
        })),
    };
  });

  // Sort: page-location order first (so all pt-1 sections precede all pt-2 sections, etc.),
  // then rank_order within each page. This keeps sections in the same page contiguous in
  // localSections, which is required for arrayMove indices to be correct during drag-and-drop.
  mapped.sort((a, b) => {
    const ai = pageOrder.indexOf(a.page_location);
    const bi = pageOrder.indexOf(b.page_location);
    const aPage = ai === -1 ? 999 : ai;
    const bPage = bi === -1 ? 999 : bi;
    if (aPage !== bPage) return aPage - bPage;
    return a.rank_order - b.rank_order;
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return mapped.map(({ _originalLoc, ...s }) => s);
}

const DroppableEmptyZone: FC<{ id: string }> = ({ id }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-2 border-dashed py-8 text-center text-sm transition-colors ${
        isOver ? "border-limestone-400 bg-limestone-50 text-text-primary" : "border-border-medium text-text-tertiary"
      }`}
    >
      {isOver ? "Release to drop here" : "Drag a section here"}
    </div>
  );
};

const SortableItem: FC<{
  id: string;
  data?: Record<string, unknown>;
  children: (dragHandleProps: Record<string, unknown>) => ReactNode;
}> = ({ id, data, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? ("relative" as const) : undefined,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...listeners, ...attributes })}
    </div>
  );
};

// --- Options & Preview Editors ---

function parseOptions(content: string | null): string[] {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content) as { options?: string[] };
    return parsed.options ?? [];
  } catch {
    return [];
  }
}

function parseAnswer(content: string | null, fieldType: string): string | string[] | null {
  const defaultVal = fieldType === FieldType.CHECKBOX ? [] : null;
  if (!content) return defaultVal;
  try {
    const parsed = JSON.parse(content) as { answer?: string | string[] | null };
    return parsed.answer ?? defaultVal;
  } catch {
    return defaultVal;
  }
}

// A WET_SIGNATURE field's content is a JSON envelope (`{"allowed_methods":
// [...]}` while authoring — `method`/`value` are only ever set at sign
// time, not through template authoring). Mirrors the backend's tolerant
// parsing (api/services/wet_signature_content.py): missing/malformed/empty
// `allowed_methods` falls back to unrestricted (all methods allowed).
function parseWetSignatureAllowedMethods(content: string | null): WetSignatureMethodValue[] {
  if (content) {
    try {
      const parsed = JSON.parse(content) as { allowed_methods?: unknown };
      const allowed = parsed.allowed_methods;
      if (
        Array.isArray(allowed) &&
        allowed.length > 0 &&
        allowed.every((m) => (WET_SIGNATURE_METHODS as readonly string[]).includes(m))
      ) {
        return allowed as WetSignatureMethodValue[];
      }
    } catch {
      // fall through to the unrestricted default below
    }
  }
  return [...WET_SIGNATURE_METHODS];
}

function WetSignatureMethodsEditor({
  allowedMethods,
  onChange,
}: {
  allowedMethods: WetSignatureMethodValue[];
  onChange: (methods: WetSignatureMethodValue[]) => void;
}) {
  return (
    <div className="space-y-2">
      <Small colorType="primary">Allowed Capture Methods</Small>
      {WET_SIGNATURE_METHODS.map((method) => (
        <label key={method} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allowedMethods.includes(method)}
            onChange={(e) => {
              const next = e.target.checked ? [...allowedMethods, method] : allowedMethods.filter((m) => m !== method);
              // At least one capture method must stay allowed — an empty
              // list round-trips through the backend's tolerant parsing as
              // "unrestricted" (all methods), which would silently disagree
              // with an editor showing nothing checked.
              if (next.length === 0) return;
              onChange(next);
            }}
            className="h-4 w-4 rounded border-border-medium text-rio-600 cursor-pointer"
          />
          <span className="text-sm text-text-primary select-none">{WET_SIGNATURE_METHOD_LABELS[method]}</span>
        </label>
      ))}
    </div>
  );
}

// Shared shape for the field-level boolean toggles (Required, Auditable) so
// each one doesn't need its own copy of the checkbox/label markup.
function FieldFlagCheckbox({
  id,
  label,
  checked,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4 rounded border-border-medium text-rio-600 cursor-pointer"
      />
      <span className="text-sm text-text-primary select-none">{label}</span>
    </label>
  );
}

export function OptionsEditor({
  options,
  onChange,
  readonly = false,
}: {
  options: string[];
  onChange: (opts: string[]) => void;
  readonly?: boolean;
}) {
  const [newOption, setNewOption] = useState("");

  const addOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed) return;
    onChange([...options, trimmed]);
    setNewOption("");
  };

  return (
    <div className="space-y-2 pl-4">
      <Small colorType="primary">Options</Small>
      {options.length === 0 && <p className="text-xs text-text-tertiary italic">No options yet. Add one below.</p>}
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={opt}
            disabled={readonly}
            onChange={(e) => {
              const next = [...options];
              next[i] = e.target.value;
              onChange(next);
            }}
            className="flex-1 rounded border border-border-medium bg-bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-400 disabled:cursor-not-allowed disabled:bg-bg-disabled disabled:text-text-tertiary"
          />
          {!readonly && (
            <button
              onClick={() => onChange(options.filter((_, j) => j !== i))}
              className="p-1 rounded text-text-tertiary hover:text-text-error-primary hover:bg-red-50"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {!readonly && (
        <div className="flex items-center gap-2 mt-1">
          <input
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOption();
              }
            }}
            placeholder="Add option…"
            className="flex-1 rounded border border-border-medium bg-bg-surface px-2 py-1 text-sm text-text-secondary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-400"
          />
          <button
            onClick={addOption}
            className="flex items-center gap-1 rounded border border-border-medium bg-bg-surface px-2 py-1 text-sm text-text-secondary hover:bg-limestone-50"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// --- Options Preview ---

function OptionsPreview({
  options,
  fieldType,
  answer,
  onChangeAnswer,
  groupId,
}: {
  options: string[];
  fieldType: string;
  answer: string | string[] | null;
  onChangeAnswer: (v: string | string[] | null) => void;
  groupId: string;
}) {
  if (!options.length) return <p className="text-xs text-text-tertiary italic">No options</p>;

  if (fieldType === FieldType.SELECT) {
    return (
      <select
        value={typeof answer === "string" ? answer : ""}
        onChange={(e) => onChangeAnswer(e.target.value || null)}
        className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary"
      >
        <option value="">Select an option...</option>
        {options.map((opt, i) => (
          <option key={i} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  if (fieldType === FieldType.CHECKBOX) {
    const checked = Array.isArray(answer) ? answer : [];
    return (
      <div className="space-y-2">
        {options.map((opt, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border-medium text-rio-600"
              checked={checked.includes(opt)}
              onChange={(e) => {
                const next = e.target.checked ? [...checked, opt] : checked.filter((v) => v !== opt);
                onChangeAnswer(next);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  // RADIO
  return (
    <div className="space-y-2">
      {options.map((opt, i) => (
        <label key={i} className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary">
          <input
            type="radio"
            name={`preview-radio-${groupId}`}
            className="h-4 w-4 border-border-medium text-rio-600"
            checked={answer === opt}
            onChange={() => onChangeAnswer(opt)}
          />
          {opt}
        </label>
      ))}
    </div>
  );
}

// --- Default Content Editor ---

function DefaultContentEditor({ content, onChange }: { content: string | null; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false);

  if (!editing && !content) {
    return (
      <div className="flex items-center justify-center h-full min-h-[80px]">
        <button onClick={() => setEditing(true)} className="text-xs text-rio-600 hover:text-rio-800">
          + Add Default Content
        </button>
      </div>
    );
  }

  return (
    <div>
      <Small colorType="primary" className="mb-1 block">
        Default Content
      </Small>
      <PlaceholderEditor
        className={EDITOR_CONTENT_INSET_CLASS}
        variant={PlaceholderEditorVariant.WYSIWYG}
        existingContent={content ?? ""}
        hintText="Type / for dynamic content"
        insertValues={PlaceholderValues}
        onChange={onChange}
        placeholder="Enter default content…"
      />
    </div>
  );
}

// --- Schema Table Preview ---

// Field types treated as table-shaped for the SCHEMA-binding invariant.
// Narrower than `TABLE_FIELD_TYPES`: only actual table renderers, since
// they consume a multi-property schema whole. Everything else is
// "single-property" and needs its ``field_identifier`` to match one key
// in the assigned prompt's ``response_schema.properties``. Backend
// mirror is ``_TABLE_FIELD_TYPES`` in api-django template_views.py.
const SCHEMA_TABLE_FIELD_TYPES = new Set<string>([
  FieldType.TABLE,
  FieldType.DSM_TABLE,
  FieldType.TREATMENT_OPTION_TABLE,
]);

// Normalize a raw response_schema (dict OR JSON string OR anything else)
// to its top-level ``properties`` keys, or null if the schema isn't a
// properties-shaped object. Mirrors the api-django
// ``_extract_schema_properties`` helper.
function getSchemaPropertyKeys(schema: unknown): string[] | null {
  let s: unknown = schema;
  if (typeof s === "string") {
    try {
      s = JSON.parse(s) as unknown;
    } catch {
      return null;
    }
  }
  if (!s || typeof s !== "object") return null;
  const obj = s as Record<string, unknown>;
  if (obj.type !== "object") return null;
  const props = obj.properties;
  if (!props || typeof props !== "object") return null;
  const keys = Object.keys(props as Record<string, unknown>);
  return keys.length > 0 ? keys : null;
}

function getSchemaColumns(schema: unknown, fieldIdentifier?: string | null): string[] | null {
  if (!schema || typeof schema !== "object") return null;
  const s = schema as Record<string, unknown>;

  // Multi-output schema: { type: "object", properties: { ServiceTable: {...}, ServiceSummary: "string" } }
  // If fieldIdentifier is provided, look only at that specific property — don't search others.
  if (fieldIdentifier && s.properties && typeof s.properties === "object") {
    const props = s.properties as Record<string, unknown>;
    if (fieldIdentifier in props) {
      return getSchemaColumns(props[fieldIdentifier]);
    }
    // fieldIdentifier specified but not found in schema — not a table field
    return null;
  }

  // Direct array: { type: "array", items: { properties: {...} } }
  if (s.type === "array") {
    const items = s.items as Record<string, unknown> | undefined;
    if (!items || typeof items !== "object") return null;
    const props = items.properties;
    if (!props || typeof props !== "object") return null;
    const ordering = items.propertyOrdering;
    if (Array.isArray(ordering) && ordering.length > 0) return ordering as string[];
    return Object.keys(props as object);
  }

  // Object wrapper (e.g. Gemini { type: "object", properties: { data: { type: "array", items: {...} } } })
  // No fieldIdentifier — recurse into each property to find the first nested array-of-objects schema.
  if (s.properties && typeof s.properties === "object") {
    for (const val of Object.values(s.properties as object)) {
      const cols = getSchemaColumns(val);
      if (cols) return cols;
    }
  }

  return null;
}

function SchemaTablePreview({ columns }: { columns: string[] }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <EyeIcon className="h-3.5 w-3.5 text-text-secondary" />
        <Small displayType="loud">Schema Preview</Small>
      </div>
      <div className="overflow-x-auto rounded-md border border-border-subtle">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-limestone-100">
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-3 py-1.5 text-left font-medium text-text-secondary border-r border-border-subtle last:border-r-0 whitespace-nowrap"
                >
                  {col.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white">
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-3 py-1.5 text-text-tertiary italic border-r border-border-subtle last:border-r-0"
                >
                  AI generated
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Sample Data Editor (AI preview content) ---

function SampleDataEditor({
  fieldLocalId,
  sampleData,
  onChange,
}: {
  fieldLocalId: string;
  sampleData: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <EyeIcon className="h-3.5 w-3.5 text-text-secondary" />
        <Small displayType="loud">AI Preview (Sample Data)</Small>
      </div>
      <PlaceholderEditor
        key={`sample-${fieldLocalId}`}
        className={EDITOR_CONTENT_INSET_CLASS}
        variant={PlaceholderEditorVariant.WYSIWYG}
        existingContent={sampleData ?? ""}
        hintText="Type / for dynamic content"
        insertValues={PlaceholderValues}
        onChange={onChange}
        placeholder="Enter sample/preview content for AI field…"
      />
    </div>
  );
}

// --- Inline Edit ---

function InlineEditText({
  value,
  onSave,
  className,
}: {
  value: string;
  onSave: (v: string) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = () => {
    const trimmed = val.trim();
    setVal(trimmed);
    onSave(trimmed);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setVal(value);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`bg-transparent border-b border-limestone-400 outline-none mr-1 min-w-0 ${className ?? ""}`}
      />
    );
  }

  return (
    <span
      className={`mr-1 truncate group/inlineedit cursor-text ${className ?? ""}`}
      // Stops single clicks from bubbling to the parent header's toggle handler.
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
        setVal(value);
      }}
      title="Double-click to edit"
    >
      {value}
      <PencilIcon
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
          setVal(value);
        }}
        className="inline-block ml-1 h-3 w-3 text-text-tertiary opacity-0 group-hover/inlineedit:opacity-100 shrink-0 cursor-pointer"
      />
    </span>
  );
}

const SectionInsertZone: FC<{ onClick: () => void }> = ({ onClick }) => (
  <div
    onClick={onClick}
    className="absolute -top-4 inset-x-0 h-4 z-20 cursor-pointer flex items-center group/section-insert"
  >
    <div className="flex-1 border-t border-rio-600 opacity-0 group-hover/section-insert:opacity-100 transition-opacity duration-200" />
    <span className="mx-2 px-2 py-0.5 text-xs bg-rio-600 text-white rounded whitespace-nowrap opacity-0 group-hover/section-insert:opacity-100 transition-opacity duration-200">
      Add Section
    </span>
    <div className="flex-1 border-t border-rio-600 opacity-0 group-hover/section-insert:opacity-100 transition-opacity duration-200" />
  </div>
);

const FieldInsertZone: FC<{ onClick: () => void }> = ({ onClick }) => (
  <div onClick={onClick} className="absolute -top-4 inset-x-0 h-4 z-20 cursor-pointer flex items-center group/insert">
    <div className="flex-1 border-t border-rio-600 opacity-0 group-hover/insert:opacity-100 transition-opacity duration-200" />
    <span className="mx-2 px-2 py-0.5 text-xs bg-rio-600 text-white rounded whitespace-nowrap opacity-0 group-hover/insert:opacity-100 transition-opacity duration-200">
      Add Field
    </span>
    <div className="flex-1 border-t border-rio-600 opacity-0 group-hover/insert:opacity-100 transition-opacity duration-200" />
  </div>
);

export const TemplateStructure: FC<{
  sections: readonly Section[];
  reportType: string;
  templateId: number;
  treatmentPlanTemplate: TreatmentPlanTemplateData | null;
  currentCurriculum: Curriculum | null;
  organizations: readonly OrganizationSummary[];
  lastUpdatedDate: string;
  isReadOnly?: boolean;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  exportFramework?: string;
}> = ({
  sections,
  reportType,
  templateId,
  treatmentPlanTemplate,
  currentCurriculum,
  organizations,
  lastUpdatedDate,
  isReadOnly = false,
  onSaved,
  onDirtyChange,
  exportFramework,
}) => {
  const queryClient = useQueryClient();
  // Show the Programming tab whenever this is an IA/RA report (the only
  // report types that ever carry a treatment plan). The tab is the home of
  // goals/domains configuration, so a TREATMENT_PLAN field in the draft
  // must always be able to reach it — even before the treatmentPlanTemplate
  // payload has been seeded for a newly generated draft.
  const showProgrammingTab = reportType === ReportType.INITIAL_ASSESSMENT || reportType === ReportType.REASSESSMENT;
  const [subTab, setSubTab] = useState<StructureSubTabValue>(StructureSubTab.NARRATIVE);

  const { data: allPromptsData } = useAllPrompts();
  const sortedAllPrompts = useMemo(
    () => (allPromptsData ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [allPromptsData],
  );
  // Restrict the picker to prompts this template is allowed to reference:
  // globals (org=null) plus any prompt owned by an org the template is
  // assigned to. Base/enterprise templates (no organizations attached)
  // stay unfiltered — those templates are cross-org by design.
  // PromptOrganization.id is a string on the wire; OrganizationSummary.id
  // is a number — normalize both sides to strings before comparison.
  const allPrompts = useMemo(() => {
    if (organizations.length === 0) return sortedAllPrompts;
    const allowedOrgIds = new Set(organizations.map((o) => String(o.id)));
    return sortedAllPrompts.filter((p) => !p.organization || allowedOrgIds.has(p.organization.id));
  }, [sortedAllPrompts, organizations]);
  // Picker options only: unmerged prompts can't run yet, so they're never offered.
  // Assigned-prompt lookups keep using allPrompts.
  const pickerPrompts = useMemo(
    () => allPrompts.filter((p) => p.registry_status === RegistryStatusEnum.matched),
    [allPrompts],
  );

  const [localSections, setLocalSections] = useState<LocalSection[]>(() => toLocal(sections, reportType));
  // Incremented each time localSections syncs from server data so that
  // TableBuilderDynamic (which has its own internal state) remounts and picks
  // up the new content. Intentionally a ref so it doesn't trigger an extra
  // render — the re-render from setLocalSections reads the updated value.
  const syncEpochRef = useRef(0);
  const [changed, setChanged] = useState(false);
  const [programmingDirty, setProgrammingDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date>(() => new Date(lastUpdatedDate));

  useEffect(() => {
    onDirtyChange?.(changed || programmingDirty);
  }, [changed, programmingDirty, onDirtyChange]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = (event: DragStartEvent) => {
    const type = event.active.data.current?.type as string | undefined;
    if (type === ClipboardItemType.FIELD) {
      setCollapsedFields((prev) => new Set(prev).add(String(event.active.id)));
    } else if (type === ClipboardItemType.SECTION) {
      setCollapsedSections((prev) => new Set(prev).add(String(event.active.id)));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const type = active.data.current?.type as string | undefined;
    if (type === ClipboardItemType.FIELD) {
      const sectionLocalId = active.data.current?.sectionLocalId as string;
      setLocalSections((prev) =>
        prev.map((s) => {
          if (s.localId !== sectionLocalId) return s;
          const oldIdx = s.fields.findIndex((f) => f.localId === active.id);
          const newIdx = s.fields.findIndex((f) => f.localId === over.id);
          if (oldIdx === -1 || newIdx === -1) return s;
          const reordered = arrayMove(s.fields, oldIdx, newIdx);
          return { ...s, fields: reordered.map((f, fi) => ({ ...f, rank_order: fi + 1 })) };
        }),
      );
    } else {
      const oldIndex = localSections.findIndex((s) => s.localId === active.id);
      // Dropped onto an empty page-location zone (over.id is the loc string, not a section localId)
      const overIsEmptyZone = oldIndex !== -1 && localSections.findIndex((s) => s.localId === over.id) === -1;
      if (overIsEmptyZone) {
        const targetPageLocation = String(over.id);
        setLocalSections((prev) =>
          prev.map((s, i) =>
            s.localId === active.id
              ? { ...s, page_location: targetPageLocation, rank_order: i + 1 }
              : { ...s, rank_order: i + 1 },
          ),
        );
      } else {
        const newIndex = localSections.findIndex((s) => s.localId === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const targetPageLocation = localSections[newIndex].page_location;
        setLocalSections((prev) => {
          const moved = arrayMove(prev, oldIndex, newIndex);
          // Assign rank_order by global array position and sync page_location for the moved section
          return moved.map((s, i) => ({
            ...s,
            rank_order: i + 1,
            page_location: s.localId === String(active.id) ? targetPageLocation : s.page_location,
          }));
        });
      }
    }
    setChanged(true);
  };

  // Re-sync from server data on background refetches, but not while the user has unsaved edits
  useEffect(() => {
    if (!changed) {
      syncEpochRef.current += 1;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalSections(toLocal(sections, reportType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  const allLocalIds = localSections.map((s) => s.localId);
  const allFieldLocalIds = localSections.flatMap((s) => s.fields.map((f) => f.localId));

  // Start all sections and fields collapsed — same as portal default
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(allLocalIds));
  const [collapsedFields, setCollapsedFields] = useState<Set<string>>(new Set(allFieldLocalIds));

  const { item: clipboardItem, copy: copyToClipboard } = useTemplateClipboard();
  const copySectionMutation = useCopySection();
  const copyFieldMutation = useCopyField();

  // A section without a title breaks report rendering downstream — empty
  // titles render as blank blocks in the exported doc. Fields intentionally
  // allow blank titles (some field types are title-less by design).
  const sectionsMissingTitle = localSections.filter((s) => !s.title.trim());
  const hasBlankSectionTitles = sectionsMissingTitle.length > 0;

  const saveMutation = useMutation({
    mutationFn: (sections: LocalSection[]) =>
      updateTemplate({
        template_id: templateId,
        report_template_sections: sections.map((s, si) => ({
          ...(s.id ? { id: s.id } : {}),
          title: s.title,
          rank_order: si + 1,
          page_location: s.page_location,
          key: s.key,
          input_dependency: s.input_dependency,
          section_fields: s.fields.map((f, fi) => ({
            ...(f.id ? { id: f.id } : {}),
            title: f.title,
            rank_order: fi + 1,
            field_type: f.field_type,
            is_required: f.is_required,
            is_auditable: f.is_auditable,
            field_identifier: f.field_identifier ?? "",
            content: convertDisplayPlaceholdersToKeys(f.content ?? "") || null,
            input_dependency: f.input_dependency,
            sample_data: f.sample_data,
            table_key: f.table_key,
            prompt_id: f.prompt?.id ?? null,
          })),
        })),
      }),
    onSuccess: (result) => {
      if (result.status === 200) {
        queryClient.setQueryData(["template", templateId], result);
        // Apply the server's persisted order immediately, bypassing the re-sync effect timing
        setLocalSections(toLocal(result.data.sections ?? [], reportType));
        notifySuccess("Structure saved");
        setChanged(false);
        setLastSavedAt(new Date());
        onSaved();
      } else {
        notifyError("Failed to save structure");
      }
    },
    onError: () => notifyError("Failed to save structure"),
  });

  const discardChanges = () => {
    setLocalSections(toLocal(sections, reportType));
    setChanged(false);
  };

  const updateSection = (localId: string, patch: Partial<LocalSection>) => {
    setLocalSections((prev) => prev.map((s) => (s.localId === localId ? { ...s, ...patch } : s)));
    setChanged(true);
  };

  const addSection = (pageLocation: string, atIndex?: number) => {
    const newSection: LocalSection = {
      localId: `new-${crypto.randomUUID()}`,
      title: "New Section",
      rank_order: localSections.filter((s) => s.page_location === pageLocation).length + 1,
      key: null,
      keyLocked: false,
      page_location: pageLocation,
      input_dependency: null,
      fields: [],
    };
    setLocalSections((prev) => {
      if (atIndex === undefined) return [...prev, newSection];
      const pageSecs = prev.filter((s) => s.page_location === pageLocation);
      if (atIndex >= pageSecs.length) return [...prev, newSection];
      const globalIdx = prev.findIndex((s) => s.localId === pageSecs[atIndex].localId);
      if (globalIdx === -1) return [...prev, newSection];
      const next = [...prev];
      next.splice(globalIdx, 0, newSection);
      return next;
    });
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.delete(newSection.localId);
      return next;
    });
    setChanged(true);
  };

  const removeSection = (localId: string) => {
    setLocalSections((prev) => prev.filter((s) => s.localId !== localId));
    setChanged(true);
  };

  const duplicateSection = (section: LocalSection) => {
    const taken = localSections
      .flatMap((s) => s.fields)
      .map((f) => f.field_identifier)
      .filter(Boolean) as string[];
    const minted: string[] = [];
    const newSection: LocalSection = {
      ...section,
      localId: `new-${crypto.randomUUID()}`,
      id: undefined,
      keyLocked: false,
      title: `${section.title} (Copy)`,
      fields: section.fields.map((f) => {
        const baseId = f.field_identifier || toSlug(`${f.title}_copy`);
        const newId = uniqueSlug(baseId, [...taken, ...minted]);
        minted.push(newId);
        return {
          ...f,
          localId: `new-${crypto.randomUUID()}`,
          id: undefined,
          field_identifier: newId,
        };
      }),
    };
    setLocalSections((prev) => {
      const idx = prev.findIndex((s) => s.localId === section.localId);
      return [...prev.slice(0, idx + 1), newSection, ...prev.slice(idx + 1)];
    });
    setChanged(true);
  };

  const updateField = (sectionLocalId: string, fieldLocalId: string, patch: Partial<LocalField>) => {
    setLocalSections((prev) =>
      prev.map((s) =>
        s.localId === sectionLocalId
          ? { ...s, fields: s.fields.map((f) => (f.localId === fieldLocalId ? { ...f, ...patch } : f)) }
          : s,
      ),
    );
    setChanged(true);
  };

  const addField = (sectionLocalId: string, atIndex?: number) => {
    const takenIds = localSections
      .flatMap((s) => s.fields)
      .map((f) => f.field_identifier)
      .filter(Boolean) as string[];
    const newField: LocalField = {
      localId: `new-${crypto.randomUUID()}`,
      title: "New Field",
      rank_order: 0,
      field_type: FieldType.SIMPLE_TEXT,
      is_required: false,
      is_auditable: false,
      field_identifier: uniqueSlug(toSlug("New Field"), takenIds),
      identifierLocked: false,
      prompt: null,
      sample_data: null,
      content: null,
      input_dependency: null,
      table_key: null,
    };
    setLocalSections((prev) =>
      prev.map((s) => {
        if (s.localId !== sectionLocalId) return s;
        const fields = [...s.fields];
        if (atIndex !== undefined) {
          fields.splice(atIndex, 0, newField);
        } else {
          fields.push(newField);
        }
        return { ...s, fields };
      }),
    );
    setCollapsedFields((prev) => {
      const next = new Set(prev);
      next.delete(newField.localId);
      return next;
    });
    setChanged(true);
  };

  const removeField = (sectionLocalId: string, fieldLocalId: string) => {
    setLocalSections((prev) =>
      prev.map((s) =>
        s.localId === sectionLocalId ? { ...s, fields: s.fields.filter((f) => f.localId !== fieldLocalId) } : s,
      ),
    );
    setChanged(true);
  };

  const duplicateField = (sectionLocalId: string, field: LocalField) => {
    const takenIds = localSections
      .flatMap((s) => s.fields)
      .map((f) => f.field_identifier)
      .filter(Boolean) as string[];
    const baseId = field.field_identifier || toSlug(`${field.title}_copy`);
    const newField: LocalField = {
      ...field,
      localId: `new-${crypto.randomUUID()}`,
      id: undefined,
      title: `${field.title} (Copy)`,
      field_identifier: uniqueSlug(baseId, takenIds),
    };
    setLocalSections((prev) =>
      prev.map((s) => {
        if (s.localId !== sectionLocalId) return s;
        const idx = s.fields.findIndex((f) => f.localId === field.localId);
        return { ...s, fields: [...s.fields.slice(0, idx + 1), newField, ...s.fields.slice(idx + 1)] };
      }),
    );
    setChanged(true);
  };

  const pasteSection = (pageLocation: string) => {
    if (!clipboardItem || clipboardItem.type !== ClipboardItemType.SECTION) return;
    copySectionMutation.mutate(
      { data: { source_section_id: clipboardItem.id, target_template_id: templateId } },
      {
        onSuccess: (res) => {
          if (res.status === 201) {
            const s = res.data as Section;
            const newLocal: LocalSection = {
              localId: String(s.id),
              id: s.id,
              title: s.title,
              rank_order: localSections.length + 1,
              key: s.key,
              keyLocked: true,
              page_location: pageLocation,
              input_dependency: (s.input_dependency as string[] | null) ?? null,
              fields: [...s.section_fields]
                .sort((a, b) => a.rank_order - b.rank_order)
                .map((f) => ({
                  localId: String(f.id),
                  id: f.id,
                  title: f.title,
                  rank_order: f.rank_order,
                  field_type: f.field_type,
                  is_required: f.is_required,
                  is_auditable: f.is_auditable,
                  field_identifier: f.field_identifier,
                  identifierLocked: true,
                  prompt: f.prompt,
                  sample_data: f.sample_data,
                  content: f.content,
                  input_dependency: (f.input_dependency as string[] | null) ?? null,
                  table_key: f.table_key,
                })),
            };
            setLocalSections((prev) => [...prev, newLocal]);
            // The local page_location reflects the user's chosen paste target — the
            // server placed the copy at the source's page_location. Mark dirty so
            // the next save reconciles by patching page_location on the new section.
            setChanged(true);
            notifySuccess(`Section "${s.title}" pasted`);
          } else {
            notifyError("Failed to paste section");
          }
        },
        onError: () => notifyError("Failed to paste section"),
      },
    );
  };

  const pasteField = (sectionLocalId: string, sectionId: number) => {
    if (!clipboardItem || clipboardItem.type !== ClipboardItemType.FIELD) return;
    copyFieldMutation.mutate(
      { data: { source_field_id: clipboardItem.id, target_section_id: sectionId } },
      {
        onSuccess: (res) => {
          if (res.status !== 201) {
            notifyError("Failed to paste field");
            return;
          }
          // copyField returns the refreshed target Section, not the new field —
          // identify the new field as the one not already in local state.
          const section = res.data as Section;
          const existingFieldIds = new Set(
            (localSections.find((s) => s.localId === sectionLocalId)?.fields ?? [])
              .map((f) => f.id)
              .filter((id): id is number => id !== undefined),
          );
          const newField = section.section_fields.find((f) => !existingFieldIds.has(f.id));
          if (!newField) {
            notifyError("Failed to paste field");
            return;
          }
          const newLocal: LocalField = {
            localId: String(newField.id),
            id: newField.id,
            title: newField.title,
            rank_order: newField.rank_order,
            field_type: newField.field_type,
            is_required: newField.is_required,
            is_auditable: newField.is_auditable,
            field_identifier: newField.field_identifier,
            identifierLocked: true,
            prompt: newField.prompt,
            sample_data: newField.sample_data,
            content: newField.content,
            input_dependency: (newField.input_dependency as string[] | null) ?? null,
            table_key: newField.table_key,
          };
          setLocalSections((prev) =>
            prev.map((s) => (s.localId === sectionLocalId ? { ...s, fields: [...s.fields, newLocal] } : s)),
          );
          setChanged(true);
          notifySuccess(`Field "${newField.title}" pasted`);
        },
        onError: () => notifyError("Failed to paste field"),
      },
    );
  };

  const toggleSection = (localId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const toggleField = (localId: string) => {
    setCollapsedFields((prev) => {
      const next = new Set(prev);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const contractAll = () => {
    setCollapsedSections(new Set(allLocalIds));
    setCollapsedFields(new Set(allFieldLocalIds));
  };

  const expandAll = () => {
    setCollapsedSections(new Set());
    setCollapsedFields(new Set());
  };

  if (!localSections.length) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border-medium p-12">
        <Small colorType="secondary">No sections in this template.</Small>
      </div>
    );
  }

  const pageLocations = getPageLocations(reportType);

  const renderSections = (sectionList: LocalSection[], pageLocation: string) =>
    sectionList.map((section, sectionIndex) => {
      const sectionCollapsed = collapsedSections.has(section.localId);
      const fieldCount = section.fields.length;
      const sortedFields = section.fields;

      return (
        <SortableItem key={section.localId} id={section.localId} data={{ type: ClipboardItemType.SECTION }}>
          {(dragHandleProps) => (
            <div className="relative">
              {sectionIndex > 0 && !isReadOnly && (
                <SectionInsertZone onClick={() => addSection(pageLocation, sectionIndex)} />
              )}
              <div
                id={section.id ? `section-${section.id}` : undefined}
                className={`flex mb-3 flex-col relative outline outline-1 outline-white rounded-2xl bg-white overflow-visible border ${
                  section.title.trim() ? "border-border-strong" : "border-red-500"
                }`}
              >
                {/* Section header */}
                <div className="flex items-center justify-between cursor-pointer pl-4 pr-2 pt-3 pb-3 group/section">
                  <div className="flex items-center flex-1 min-w-0" onClick={() => toggleSection(section.localId)}>
                    <button
                      {...dragHandleProps}
                      onClick={(e) => e.stopPropagation()}
                      className="h-6 w-6 p-1 mr-1 rounded hover:bg-limestone-50 text-text-tertiary shrink-0 cursor-grab active:cursor-grabbing touch-none"
                      aria-label="Drag to reorder"
                    >
                      <Bars3Icon className="h-4 w-4" />
                    </button>
                    <div className="h-6 w-6 p-1 mr-2 rounded hover:bg-limestone-50 text-text-secondary shrink-0">
                      <ChevronDownIcon className={`h-4 w-4 ${sectionCollapsed ? "hidden" : ""}`} />
                      <ChevronRightIcon className={`h-4 w-4 ${sectionCollapsed ? "" : "hidden"}`} />
                    </div>
                    <InlineEditText
                      value={section.title}
                      onSave={(v) => {
                        if (v && !section.keyLocked) {
                          const takenKeys = localSections
                            .filter((s) => s.localId !== section.localId)
                            .map((s) => s.key)
                            .filter(Boolean) as string[];
                          const newKey = uniqueSlug(toSlug(v), takenKeys);
                          updateSection(section.localId, { title: v, key: newKey, keyLocked: true });
                        } else {
                          updateSection(section.localId, { title: v });
                        }
                      }}
                      className="font-semibold text-text-primary"
                    />
                    {!section.title.trim() && (
                      <span
                        className="ml-1 shrink-0 rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Title required
                      </span>
                    )}
                    {section.key && (
                      <EditableKeyBadge
                        value={section.key}
                        label="section key"
                        takenKeys={
                          localSections
                            .filter((s) => s.localId !== section.localId)
                            .map((s) => s.key)
                            .filter(Boolean) as string[]
                        }
                        onSave={(v) => updateSection(section.localId, { key: v })}
                      />
                    )}
                    {fieldCount > 0 && (
                      <div className="flex items-center gap-0.5 ml-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          title="Collapse all fields"
                          onClick={() => {
                            setCollapsedFields((prev) => {
                              const next = new Set(prev);
                              section.fields.forEach((f) => next.add(f.localId));
                              return next;
                            });
                            setCollapsedSections((prev) => new Set(prev).add(section.localId));
                          }}
                          className="h-5 w-5 p-0.5 rounded hover:bg-limestone-50 text-text-tertiary"
                        >
                          <ArrowsPointingInIcon className="h-4 w-4" />
                        </button>
                        <button
                          title="Expand all fields"
                          onClick={() => {
                            setCollapsedFields((prev) => {
                              const next = new Set(prev);
                              section.fields.forEach((f) => next.delete(f.localId));
                              return next;
                            });
                            setCollapsedSections((prev) => {
                              const next = new Set(prev);
                              next.delete(section.localId);
                              return next;
                            });
                          }}
                          className="h-5 w-5 p-0.5 rounded hover:bg-limestone-50 text-text-tertiary"
                        >
                          <ArrowsPointingOutIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mr-2">
                    {section.input_dependency?.length ? (
                      <span className="text-xs text-rio-600 font-medium">
                        {section.input_dependency.length} source{section.input_dependency.length !== 1 ? "s" : ""}
                      </span>
                    ) : null}
                    <SectionActionMenu
                      onAddField={() => {
                        updateSection(section.localId, { ...section });
                        if (sectionCollapsed) toggleSection(section.localId);
                        addField(section.localId);
                      }}
                      onDuplicate={() => duplicateSection(section)}
                      onDelete={() => removeSection(section.localId)}
                      onDataSources={(deps) => updateSection(section.localId, { input_dependency: deps })}
                      onCopy={() => {
                        if (section.id) {
                          copyToClipboard({ type: ClipboardItemType.SECTION, id: section.id, title: section.title });
                          notifySuccess(`"${section.title}" copied — scroll down to paste`);
                        }
                      }}
                      currentDataSources={section.input_dependency ?? []}
                      sectionTitle={section.title}
                      fieldCount={fieldCount}
                      reportType={reportType}
                    />
                  </div>
                </div>

                {/* Section body */}
                {!sectionCollapsed && (
                  <>
                    <div className="mb-4 border-t w-full border-limestone-100" />
                    <div className="px-4 pb-4 space-y-4">
                      {fieldCount === 0 ? (
                        <div className="flex flex-col items-center py-8 gap-3">
                          <Small colorType="secondary">No Fields in this Section</Small>
                          {!isReadOnly && (
                            <button
                              onClick={() => addField(section.localId)}
                              className="flex items-center gap-1.5 rounded-lg border border-dashed border-border-medium px-4 py-2 text-sm text-text-secondary hover:bg-bg-default"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Add Field
                            </button>
                          )}
                        </div>
                      ) : (
                        <SortableContext
                          items={sortedFields.map((f) => f.localId)}
                          strategy={verticalListSortingStrategy}
                        >
                          {sortedFields.map((field, fieldIndex) => {
                            const fieldCollapsed = collapsedFields.has(field.localId);
                            const isAi = !!field.prompt;
                            const typeLabel =
                              FIELD_TYPE_LABELS[field.field_type] ?? field.field_type?.replace(/_/g, " ") ?? "—";
                            const usesOptions = (FIELD_TYPES_WITH_OPTIONS as readonly string[]).includes(
                              field.field_type,
                            );
                            const isTable = field.field_type === FieldType.TABLE;
                            const isComplexType =
                              (TABLE_FIELD_TYPES as readonly string[]).includes(field.field_type) && !usesOptions;
                            const tableKeyLocked = (
                              [
                                FieldType.ASSESSMENT_APPOINTMENTS,
                                FieldType.COORDINATION_OF_CARE,
                                FieldType.DSM_TABLE,
                                FieldType.TREATMENT_OPTION_TABLE,
                                FieldType.ASSESSMENT_IMAGE,
                              ] as readonly string[]
                            ).includes(field.field_type);

                            return (
                              <SortableItem
                                key={field.localId}
                                id={field.localId}
                                data={{ type: ClipboardItemType.FIELD, sectionLocalId: section.localId }}
                              >
                                {(fieldDragHandleProps) => (
                                  <div className="relative">
                                    {!isReadOnly && (
                                      <FieldInsertZone onClick={() => addField(section.localId, fieldIndex)} />
                                    )}
                                    <div
                                      id={field.id ? `field-${field.id}` : undefined}
                                      className={`flex flex-col relative outline outline-1 outline-white rounded-2xl overflow-visible ${isAi ? "border border-amethyst-400 bg-amethyst-50" : "border border-border-strong bg-white"}`}
                                    >
                                      {/* Field header */}
                                      <div className="flex items-center justify-between cursor-pointer pl-4 pr-2 pt-3 pb-3 group/field">
                                        <div
                                          className="flex items-center flex-1 min-w-0"
                                          onClick={() => toggleField(field.localId)}
                                        >
                                          <button
                                            {...fieldDragHandleProps}
                                            onClick={(e) => e.stopPropagation()}
                                            className="h-6 w-6 p-1 mr-1 rounded hover:bg-limestone-50 text-text-tertiary shrink-0 cursor-grab active:cursor-grabbing touch-none"
                                            aria-label="Drag to reorder"
                                          >
                                            <Bars3Icon className="h-4 w-4" />
                                          </button>
                                          <div className="h-6 w-6 p-1 mr-2 rounded hover:bg-limestone-50 text-text-secondary shrink-0">
                                            <ChevronDownIcon className={`h-4 w-4 ${fieldCollapsed ? "hidden" : ""}`} />
                                            <ChevronRightIcon className={`h-4 w-4 ${fieldCollapsed ? "" : "hidden"}`} />
                                          </div>
                                          <InlineEditText
                                            value={field.title}
                                            onSave={(v) => {
                                              if (v && !field.identifierLocked) {
                                                const takenIds = localSections
                                                  .flatMap((s) => s.fields)
                                                  .filter((f) => f.localId !== field.localId)
                                                  .map((f) => f.field_identifier)
                                                  .filter(Boolean) as string[];
                                                const newId = uniqueSlug(toSlug(v), takenIds);
                                                updateField(section.localId, field.localId, {
                                                  title: v,
                                                  field_identifier: newId,
                                                  identifierLocked: true,
                                                });
                                              } else {
                                                updateField(section.localId, field.localId, { title: v });
                                              }
                                            }}
                                            className="text-sm font-medium text-text-primary"
                                          />
                                          {field.field_identifier && (
                                            <EditableKeyBadge
                                              value={field.field_identifier}
                                              label="field identifier"
                                              takenKeys={
                                                localSections
                                                  .flatMap((s) => s.fields)
                                                  .filter((f) => f.localId !== field.localId)
                                                  .map((f) => f.field_identifier)
                                                  .filter(Boolean) as string[]
                                              }
                                              onSave={(v) =>
                                                updateField(section.localId, field.localId, {
                                                  field_identifier: v,
                                                  identifierLocked: true,
                                                })
                                              }
                                              exportFramework={exportFramework}
                                              sectionKey={section.key ?? undefined}
                                            />
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 mr-2">
                                          {isAi ? (
                                            <Badge appearance="ai" text="AI" className="animate-none!" />
                                          ) : (
                                            <Badge appearance="neutral" text="Default" className="animate-none!" />
                                          )}
                                          {field.input_dependency?.length ? (
                                            <span className="text-xs text-rio-600 font-medium">
                                              {field.input_dependency.length}s
                                            </span>
                                          ) : null}
                                          <FieldActionMenu
                                            onDuplicate={() => duplicateField(section.localId, field)}
                                            onDelete={() => removeField(section.localId, field.localId)}
                                            onDataSources={(deps) =>
                                              updateField(section.localId, field.localId, { input_dependency: deps })
                                            }
                                            onCopy={() => {
                                              if (field.id) {
                                                copyToClipboard({
                                                  type: ClipboardItemType.FIELD,
                                                  id: field.id,
                                                  title: field.title,
                                                });
                                                notifySuccess(`"${field.title}" copied — scroll down to paste`);
                                              }
                                            }}
                                            currentDataSources={field.input_dependency ?? []}
                                            fieldTitle={field.title}
                                            reportType={reportType}
                                          />
                                        </div>
                                      </div>

                                      {/* Field body */}
                                      {!fieldCollapsed && (
                                        <>
                                          <div className="mb-4 border-t w-full border-limestone-100" />
                                          <div className="px-4 pb-4">
                                            {field.field_type === FieldType.TREATMENT_PLAN ? (
                                              <div className="bg-bg-default border border-border-strong rounded-lg p-4">
                                                <div className="flex flex-col items-center gap-3 py-4">
                                                  <Small colorType="secondary">
                                                    Treatment plan goals and domains are configured in the Programming
                                                    tab.
                                                  </Small>
                                                  {showProgrammingTab && (
                                                    <Button
                                                      text="Go to Programming"
                                                      appearance="secondary"
                                                      onClick={() => setSubTab("programming")}
                                                    />
                                                  )}
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="bg-bg-default border border-border-strong rounded-lg p-4 space-y-3">
                                                {(() => {
                                                  const assignedPrompt = field.prompt
                                                    ? allPrompts.find((p) => p.id === field.prompt?.id)
                                                    : null;
                                                  const isSchemaTable = SCHEMA_TABLE_FIELD_TYPES.has(field.field_type);
                                                  // Table schemas often use ``{type: "array", items: {properties: ...}}``
                                                  // which ``getSchemaPropertyKeys`` doesn't recognize (that helper is
                                                  // tuned for the single-property binding). Any non-null response_schema
                                                  // means the prompt returns something structured — don't warn.
                                                  const tableMissingSchema =
                                                    isSchemaTable &&
                                                    !!assignedPrompt &&
                                                    !assignedPrompt.response_schema;
                                                  if (!tableMissingSchema) return null;
                                                  return (
                                                    <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                                                      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                                                      <span>
                                                        The AI prompt on this field returns plain text, not table rows.
                                                        This table may not fill in.
                                                      </span>
                                                    </div>
                                                  );
                                                })()}
                                                {/* Row 1: Input Type + Required + AI Prompt inline */}
                                                <div className="flex items-end gap-4 flex-wrap">
                                                  <div>
                                                    <Small colorType="primary" className="mb-1 block">
                                                      Input Type
                                                    </Small>
                                                    <select
                                                      value={field.field_type}
                                                      onChange={(e) => {
                                                        const newType = e.target.value;
                                                        const isNewOptions = (
                                                          FIELD_TYPES_WITH_OPTIONS as readonly string[]
                                                        ).includes(newType);
                                                        let newContent = field.content;
                                                        if (
                                                          isNewOptions &&
                                                          !(FIELD_TYPES_WITH_OPTIONS as readonly string[]).includes(
                                                            field.field_type,
                                                          )
                                                        ) {
                                                          const answer = newType === FieldType.CHECKBOX ? "[]" : "null";
                                                          newContent = `{"options": ["Option 1", "Option 2"], "answer": ${answer}}`;
                                                        }
                                                        updateField(section.localId, field.localId, {
                                                          field_type: newType,
                                                          content: newContent,
                                                        });
                                                      }}
                                                      className="block rounded-md border border-border-medium bg-bg-surface pl-3 pr-8 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
                                                    >
                                                      {Object.entries(FIELD_TYPE_LABELS).map(([val, label]) => (
                                                        <option key={val} value={val}>
                                                          {label}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  {allPrompts.length > 0 &&
                                                    (() => {
                                                      const assignedPrompt = field.prompt
                                                        ? allPrompts.find((p) => p.id === field.prompt?.id)
                                                        : null;
                                                      const schemaKeys = assignedPrompt
                                                        ? getSchemaPropertyKeys(assignedPrompt.response_schema)
                                                        : null;
                                                      const isSchemaTable = SCHEMA_TABLE_FIELD_TYPES.has(
                                                        field.field_type,
                                                      );
                                                      const needsPropertyBinding = !!schemaKeys && !isSchemaTable;
                                                      const currentIdMatchesSchema =
                                                        !!schemaKeys &&
                                                        !!field.field_identifier &&
                                                        schemaKeys.includes(field.field_identifier);
                                                      return (
                                                        <div className="flex flex-wrap items-start gap-3">
                                                          <div className="min-w-64 flex-1">
                                                            <Small colorType="primary" className="mb-1 block">
                                                              AI Prompt
                                                            </Small>
                                                            <PromptDropdown
                                                              value={field.prompt}
                                                              options={pickerPrompts}
                                                              reportType={reportType}
                                                              onChange={(selected) => {
                                                                const nextSchemaKeys = selected
                                                                  ? getSchemaPropertyKeys(selected.response_schema)
                                                                  : null;
                                                                const nextIsSchemaTable = SCHEMA_TABLE_FIELD_TYPES.has(
                                                                  field.field_type,
                                                                );
                                                                // When picking a multi-property prompt on a
                                                                // non-table field whose current identifier
                                                                // isn't in the new schema, auto-set the
                                                                // identifier to the first property so the
                                                                // report engine has a valid route. The user
                                                                // can then swap via the selector next to it.
                                                                const autoBindIdentifier =
                                                                  selected &&
                                                                  !nextIsSchemaTable &&
                                                                  nextSchemaKeys &&
                                                                  !nextSchemaKeys.includes(field.field_identifier ?? "")
                                                                    ? nextSchemaKeys[0]
                                                                    : null;
                                                                updateField(section.localId, field.localId, {
                                                                  prompt: selected
                                                                    ? {
                                                                        id: selected.id,
                                                                        key: selected.key,
                                                                        name: selected.name,
                                                                      }
                                                                    : null,
                                                                  ...(selected ? {} : { sample_data: null }),
                                                                  ...(autoBindIdentifier
                                                                    ? {
                                                                        field_identifier: autoBindIdentifier,
                                                                        identifierLocked: true,
                                                                      }
                                                                    : {}),
                                                                });
                                                              }}
                                                            />
                                                            {/* Full list, not the org-filtered picker options, so it agrees
                                                                with the publish and Details checks. */}
                                                            <PromptReportTypeBadge
                                                              prompt={
                                                                field.prompt
                                                                  ? sortedAllPrompts.find(
                                                                      (p) => p.id === field.prompt?.id,
                                                                    )
                                                                  : null
                                                              }
                                                              reportType={reportType}
                                                            />
                                                          </div>
                                                          {needsPropertyBinding && (
                                                            <div className="min-w-48 flex-1">
                                                              <Small
                                                                colorType="primary"
                                                                className="mb-1 flex items-center gap-1"
                                                              >
                                                                Which AI output shows here?
                                                                <span
                                                                  tabIndex={0}
                                                                  className="group relative inline-flex focus:outline-none"
                                                                >
                                                                  <InformationCircleIcon className="h-3.5 w-3.5 text-text-tertiary" />
                                                                  <span
                                                                    role="tooltip"
                                                                    className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 hidden w-64 -translate-x-1/2 rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal leading-snug text-white shadow-lg group-hover:block group-focus:block"
                                                                  >
                                                                    This AI prompt returns several outputs. Pick which
                                                                    one shows in this field.
                                                                  </span>
                                                                </span>
                                                              </Small>
                                                              <select
                                                                value={
                                                                  currentIdMatchesSchema
                                                                    ? (field.field_identifier ?? "")
                                                                    : ""
                                                                }
                                                                onChange={(e) => {
                                                                  const next = e.target.value;
                                                                  if (!next) return;
                                                                  updateField(section.localId, field.localId, {
                                                                    field_identifier: next,
                                                                    identifierLocked: true,
                                                                  });
                                                                }}
                                                                className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
                                                              >
                                                                {!currentIdMatchesSchema && (
                                                                  <option value="" disabled>
                                                                    Choose one…
                                                                  </option>
                                                                )}
                                                                {schemaKeys.map((k) => (
                                                                  <option key={k} value={k}>
                                                                    {k}
                                                                  </option>
                                                                ))}
                                                              </select>
                                                              {!currentIdMatchesSchema && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                  This field is set to &quot;
                                                                  {field.field_identifier || "(empty)"}&quot;, which
                                                                  isn&apos;t one of the AI&apos;s outputs (
                                                                  {schemaKeys.join(", ")}). It won&apos;t show anything
                                                                  until you pick one from the list.
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })()}
                                                  <div className="flex items-center gap-4 pb-1.5">
                                                    <FieldFlagCheckbox
                                                      id={`required-${field.localId}`}
                                                      label="Required"
                                                      checked={field.is_required}
                                                      onToggle={(checked) =>
                                                        updateField(section.localId, field.localId, {
                                                          is_required: checked,
                                                        })
                                                      }
                                                    />
                                                    <FieldFlagCheckbox
                                                      id={`auditable-${field.localId}`}
                                                      label="Auditable"
                                                      checked={field.is_auditable}
                                                      onToggle={(checked) =>
                                                        updateField(section.localId, field.localId, {
                                                          is_auditable: checked,
                                                        })
                                                      }
                                                    />
                                                  </div>
                                                </div>

                                                {/* Table Key — only for table field types that also have data sources assigned */}
                                                {(isTable || tableKeyLocked) &&
                                                  (field.input_dependency?.length ?? 0) > 0 && (
                                                    <div>
                                                      <Small colorType="primary" className="mb-1 block">
                                                        Table Key
                                                      </Small>
                                                      {tableKeyLocked ? (
                                                        <>
                                                          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-disabled px-3 py-1.5">
                                                            <span className="text-sm font-mono text-text-secondary">
                                                              {field.table_key ?? "—"}
                                                            </span>
                                                            <span className="ml-auto text-xs text-text-tertiary italic">
                                                              read-only
                                                            </span>
                                                          </div>
                                                          <p className="mt-1 text-xs text-text-tertiary">
                                                            This key is fixed — it must match exactly for the report
                                                            engine to render this field type.
                                                          </p>
                                                        </>
                                                      ) : (
                                                        <>
                                                          <input
                                                            value={field.table_key ?? ""}
                                                            onChange={(e) =>
                                                              updateField(section.localId, field.localId, {
                                                                table_key: e.target.value || null,
                                                              })
                                                            }
                                                            placeholder="e.g. assessment_table_1"
                                                            className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm font-mono text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500"
                                                          />
                                                          <p className="mt-1 text-xs text-text-tertiary">
                                                            Links this field to a specific data source table for export.
                                                          </p>
                                                        </>
                                                      )}
                                                    </div>
                                                  )}

                                                {/* Options + Preview side by side */}
                                                {usesOptions && (
                                                  <div className="grid grid-cols-2 gap-4 items-start">
                                                    <OptionsEditor
                                                      options={parseOptions(field.content)}
                                                      onChange={(opts) => {
                                                        const answer =
                                                          field.field_type === FieldType.CHECKBOX ? "[]" : "null";
                                                        updateField(section.localId, field.localId, {
                                                          content: `{"options": ${JSON.stringify(opts)}, "answer": ${answer}}`,
                                                        });
                                                      }}
                                                    />
                                                    <div className="border border-limestone-100 rounded-md overflow-hidden">
                                                      <div className="bg-limestone-100 flex items-center px-2 py-1 gap-1.5">
                                                        <EyeIcon className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                                                        <Small displayType="loud">Preview</Small>
                                                      </div>
                                                      <div className="bg-white p-3">
                                                        <p className="font-semibold text-sm text-text-primary mb-2">
                                                          {field.title}
                                                        </p>
                                                        <OptionsPreview
                                                          options={parseOptions(field.content)}
                                                          fieldType={field.field_type}
                                                          answer={parseAnswer(field.content, field.field_type)}
                                                          groupId={field.localId}
                                                          onChangeAnswer={(newAnswer) => {
                                                            const opts = parseOptions(field.content);
                                                            updateField(section.localId, field.localId, {
                                                              content: JSON.stringify({
                                                                options: opts,
                                                                answer: newAnswer,
                                                              }),
                                                            });
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}

                                                {/* TABLE field — full-width editable TableBuilderDynamic */}
                                                {!isAi && isTable && (
                                                  <div>
                                                    <Small colorType="primary" className="mb-1 block">
                                                      Table Structure
                                                    </Small>
                                                    <TableBuilderDynamic
                                                      key={`table-${field.localId}-${syncEpochRef.current}`}
                                                      tableSaving={false}
                                                      title=""
                                                      tableId={field.localId}
                                                      displayOnlyMode={false}
                                                      editMode="full"
                                                      showEditButton={false}
                                                      showAutoSaveMessage={false}
                                                      jsonDatum={parseTableContent(field.content)}
                                                      onSave={async (datum) =>
                                                        updateField(section.localId, field.localId, {
                                                          content: JSON.stringify(datum),
                                                        })
                                                      }
                                                    />
                                                  </div>
                                                )}

                                                {/* Engine-rendered data tables — no editable content */}
                                                {!isAi &&
                                                  [
                                                    "ASSESSMENT_APPOINTMENTS",
                                                    "COORDINATION_OF_CARE",
                                                    "DSM_TABLE",
                                                    "TREATMENT_OPTION_TABLE",
                                                  ].includes(field.field_type) && (
                                                    <div className="border border-limestone-100 rounded-md p-3 bg-bg-surface flex items-center gap-2">
                                                      <span className="text-xs font-medium text-text-secondary">
                                                        {typeLabel}
                                                      </span>
                                                      <span className="text-xs text-text-tertiary">
                                                        — rendered by the report engine at export time
                                                      </span>
                                                    </div>
                                                  )}

                                                {/* Date / DateTime */}
                                                {!isAi &&
                                                  (field.field_type === FieldType.DATE ||
                                                    field.field_type === FieldType.DATETIME) && (
                                                    <div>
                                                      <Small colorType="primary" className="mb-1 block">
                                                        Preview
                                                      </Small>
                                                      <input
                                                        type={
                                                          field.field_type === FieldType.DATE
                                                            ? "date"
                                                            : "datetime-local"
                                                        }
                                                        readOnly
                                                        className="block rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary"
                                                      />
                                                    </div>
                                                  )}

                                                {/* Image / Assessment Image — disabled since uploading here makes no sense */}
                                                {!isAi &&
                                                  (field.field_type === FieldType.IMAGE ||
                                                    field.field_type === FieldType.ASSESSMENT_IMAGE) && (
                                                    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-medium bg-bg-surface px-6 py-8 text-text-tertiary cursor-not-allowed">
                                                      <PhotoIcon className="h-8 w-8" />
                                                      <span className="text-sm">
                                                        Image upload — filled in at report time
                                                      </span>
                                                    </div>
                                                  )}

                                                {/* Blank Signature Line */}
                                                {!isAi && field.field_type === FieldType.BLANK_SIGNATURE_LINE && (
                                                  <div className="py-4 flex flex-col items-start gap-1">
                                                    <div className="w-48 border-b-2 border-text-primary" />
                                                    <span className="text-xs text-text-tertiary">Signature</span>
                                                  </div>
                                                )}

                                                {/* Digital Signature */}
                                                {!isAi && field.field_type === FieldType.TYPED_SIGNATURE && (
                                                  <div className="rounded-lg border border-border-medium bg-limestone-50 p-4">
                                                    <input
                                                      type="text"
                                                      placeholder="Enter signature"
                                                      className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary italic"
                                                    />
                                                  </div>
                                                )}

                                                {/* Wet Signature */}
                                                {!isAi && field.field_type === FieldType.WET_SIGNATURE && (
                                                  <WetSignatureMethodsEditor
                                                    allowedMethods={parseWetSignatureAllowedMethods(field.content)}
                                                    onChange={(methods) =>
                                                      updateField(section.localId, field.localId, {
                                                        content: JSON.stringify({ allowed_methods: methods }),
                                                      })
                                                    }
                                                  />
                                                )}

                                                {/* Default Content + Preview side by side (SIMPLE_TEXT, WYSIWYG, FORMATTED_TEXT) */}
                                                {!isAi && !usesOptions && !isComplexType && (
                                                  <div className="grid grid-cols-2 gap-4 items-stretch">
                                                    <DefaultContentEditor
                                                      content={field.content}
                                                      onChange={(v) =>
                                                        updateField(section.localId, field.localId, { content: v })
                                                      }
                                                    />
                                                    <div className="border border-limestone-100 rounded-md overflow-hidden">
                                                      <div className="bg-limestone-100 flex items-center px-2 py-1 gap-1.5">
                                                        <EyeIcon className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                                                        <Small displayType="loud">Preview</Small>
                                                      </div>
                                                      <div className="bg-white p-3">
                                                        <p className="font-semibold text-sm text-text-primary mb-2">
                                                          {field.title}
                                                        </p>
                                                        {field.content ? (
                                                          <div className="max-w-full overflow-hidden">
                                                            <WYSIWYG
                                                              key={`prev-${field.localId}`}
                                                              displayOnlyMode={true}
                                                              existingContent={convertDisplayPlaceholdersToKeys(
                                                                field.content ?? "",
                                                              )}
                                                              onSave={async () => {}}
                                                              isSaving={false}
                                                              title=""
                                                              placeholder=""
                                                            />
                                                          </div>
                                                        ) : (
                                                          <p className="text-sm text-text-tertiary italic">
                                                            {typeLabel} field — no default content
                                                          </p>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Sample Data editor (AI fields — IS the preview) */}
                                                {isAi &&
                                                  (() => {
                                                    const fullPrompt = allPrompts.find(
                                                      (p) => p.id === field.prompt?.id,
                                                    );
                                                    const schemaColumns = fullPrompt
                                                      ? getSchemaColumns(
                                                          fullPrompt.response_schema,
                                                          field.field_identifier,
                                                        )
                                                      : null;
                                                    return (
                                                      <>
                                                        {schemaColumns && (
                                                          <SchemaTablePreview columns={schemaColumns} />
                                                        )}
                                                        {!schemaColumns && fullPrompt?.sample_output && (
                                                          <div>
                                                            <div className="flex items-center gap-1 mb-1">
                                                              <EyeIcon className="h-3.5 w-3.5 text-text-secondary" />
                                                              <Small displayType="loud">Sample Output</Small>
                                                            </div>
                                                            <SampleOutputPreview
                                                              value={fullPrompt.sample_output}
                                                              className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm overflow-y-auto max-h-64"
                                                            />
                                                          </div>
                                                        )}
                                                        {!schemaColumns && !fullPrompt?.sample_output && (
                                                          <SampleDataEditor
                                                            fieldLocalId={field.localId}
                                                            sampleData={field.sample_data}
                                                            onChange={(v) =>
                                                              updateField(section.localId, field.localId, {
                                                                sample_data: v,
                                                              })
                                                            }
                                                          />
                                                        )}
                                                      </>
                                                    );
                                                  })()}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </SortableItem>
                            );
                          })}
                        </SortableContext>
                      )}

                      {/* End-of-section insert zone */}
                      {fieldCount > 0 && !isReadOnly && (
                        <div className="relative h-4">
                          <FieldInsertZone onClick={() => addField(section.localId, sortedFields.length)} />
                        </div>
                      )}

                      {/* Paste field */}
                      {clipboardItem?.type === ClipboardItemType.FIELD && section.id && (
                        <button
                          onClick={() => pasteField(section.localId, section.id!)}
                          disabled={copyFieldMutation.isPending}
                          className="flex items-center gap-1.5 justify-center rounded-lg border border-dashed border-amethyst-400 bg-amethyst-50 px-3 py-2 text-sm text-amethyst-700 hover:bg-amethyst-100 disabled:opacity-50"
                        >
                          <ArrowRightCircleIcon className="h-4 w-4" />
                          Paste "{clipboardItem.title}"
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </SortableItem>
      );
    });

  return (
    <div className={!isReadOnly ? "pb-24" : "pb-12"}>
      {/* Sub-tabs (Narrative / Programming) */}
      {showProgrammingTab && (
        <div className="flex gap-4 mb-4 border-b border-border-subtle">
          {(
            [
              [StructureSubTab.NARRATIVE, "Narrative"],
              [StructureSubTab.PROGRAMMING, "Programming"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setSubTab(id)}
              className={`py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                subTab === id
                  ? "border-limestone-900 text-limestone-900"
                  : "border-transparent text-text-secondary hover:text-text-primary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Programming tab */}
      {showProgrammingTab && subTab === StructureSubTab.PROGRAMMING && treatmentPlanTemplate && (
        <ProgrammingTab
          templateId={templateId}
          treatmentPlanTemplate={treatmentPlanTemplate}
          currentCurriculum={currentCurriculum}
          organizations={organizations}
          onDirtyChange={setProgrammingDirty}
          onSaved={onSaved}
          isReadOnly={isReadOnly}
        />
      )}
      {showProgrammingTab && subTab === StructureSubTab.PROGRAMMING && !treatmentPlanTemplate && (
        <div className="rounded-lg border border-border-subtle bg-bg-default p-8 text-center">
          <Small colorType="secondary">
            No treatment plan template is attached to this draft yet. Goals and domains will appear here once one is
            configured.
          </Small>
        </div>
      )}

      {/* Narrative tab toolbar (only shown when narrative is active) */}
      <div
        className={`flex items-center justify-between mb-4 ${showProgrammingTab && subTab === StructureSubTab.PROGRAMMING ? "hidden" : ""}`}
      >
        <div className="flex items-center gap-3">
          <button onClick={contractAll} className="flex items-center gap-1 text-sm text-rio-600 hover:text-rio-800">
            <ArrowsPointingInIcon className="h-4 w-4" />
            <Small colorType="primary" className="text-rio-600">
              Contract All
            </Small>
          </button>
          <button onClick={expandAll} className="flex items-center gap-1 text-sm text-rio-600 hover:text-rio-800">
            <ArrowsPointingOutIcon className="h-4 w-4" />
            <Small colorType="primary" className="text-rio-600">
              Expand All
            </Small>
          </button>
        </div>
      </div>

      {/* Grid: sections (col-span-3) + sticky nav (col-span-1) */}
      <div
        className={`grid grid-cols-4 gap-x-6 ${showProgrammingTab && subTab === StructureSubTab.PROGRAMMING ? "hidden" : ""}`}
      >
        <div className="col-span-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={localSections.map((s) => s.localId)} strategy={verticalListSortingStrategy}>
              {pageLocations ? (
                pageLocations.map((loc, locIdx) => {
                  const pageSections = localSections.filter((s) => s.page_location === loc);
                  return (
                    <React.Fragment key={loc}>
                      <div className={`flex items-center gap-3 mb-3 ${locIdx > 0 ? "mt-6" : ""}`}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                          {PAGE_LOCATION_LABELS[loc] ?? loc}
                        </span>
                        <div className="flex-1 h-px bg-border-subtle" />
                      </div>
                      {pageSections.length === 0 ? (
                        <>
                          <DroppableEmptyZone id={loc} />
                          {!isReadOnly && (
                            <div className="flex gap-2 mt-2 mb-2">
                              <button
                                onClick={() => addSection(loc)}
                                className="flex items-center gap-1.5 flex-1 justify-center rounded-lg border border-dashed border-border-medium px-4 py-2 text-sm text-text-secondary hover:bg-bg-default"
                              >
                                <PlusIcon className="h-4 w-4" />
                                Add Section to {PAGE_LOCATION_LABELS[loc] ?? loc}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {renderSections(pageSections, loc)}
                          {!isReadOnly && (
                            <div className="relative h-4 mb-2">
                              <SectionInsertZone onClick={() => addSection(loc, pageSections.length)} />
                            </div>
                          )}
                        </>
                      )}
                      {clipboardItem?.type === ClipboardItemType.SECTION && (
                        <div className="flex gap-2 mt-2 mb-2">
                          <button
                            onClick={() => pasteSection(loc)}
                            disabled={copySectionMutation.isPending}
                            className="flex items-center gap-1.5 justify-center rounded-lg border border-dashed border-limestone-400 bg-limestone-50 px-3 py-2 text-sm text-text-secondary hover:bg-limestone-100 disabled:opacity-50"
                          >
                            <ArrowRightCircleIcon className="h-4 w-4" />
                            Paste "{clipboardItem.title}"
                          </button>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <>
                  {renderSections(localSections, localSections[0]?.page_location ?? "report-part-1")}
                  {!isReadOnly && (
                    <div className="relative h-4 mt-1">
                      <SectionInsertZone
                        onClick={() =>
                          addSection(localSections[0]?.page_location ?? "report-part-1", localSections.length)
                        }
                      />
                    </div>
                  )}
                  {clipboardItem?.type === ClipboardItemType.SECTION && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => pasteSection(localSections[0]?.page_location ?? "report-part-1")}
                        disabled={copySectionMutation.isPending}
                        className="flex items-center gap-1.5 justify-center rounded-lg border border-dashed border-limestone-400 bg-limestone-50 px-3 py-2 text-sm text-text-secondary hover:bg-limestone-100 disabled:opacity-50"
                      >
                        <ArrowRightCircleIcon className="h-4 w-4" />
                        Paste "{clipboardItem.title}"
                      </button>
                    </div>
                  )}
                </>
              )}
            </SortableContext>
          </DndContext>
        </div>

        {/* Sticky "On This Page" nav */}
        <div className="col-span-1">
          <div className="sticky top-6 bg-white rounded-2xl py-4 px-4 border border-limestone-100">
            <div className="flex items-center gap-2 mb-3">
              <BookOpenIcon className="h-4 w-4 text-text-secondary" />
              <Small displayType="loud" colorType="primary">
                On This Page
              </Small>
            </div>
            {pageLocations
              ? pageLocations.map((loc) => {
                  const pageSections = localSections.filter((s) => s.page_location === loc);
                  if (!pageSections.length) return null;
                  return (
                    <div key={loc} className="mb-3">
                      <span className="inline-block text-xs font-semibold bg-limestone-50 rounded px-2 py-1 text-text-primary mb-1">
                        {PAGE_LOCATION_LABELS[loc] ?? loc}
                      </span>
                      {pageSections.map((section) => (
                        <a
                          key={section.localId}
                          href={section.id ? `#section-${section.id}` : undefined}
                          className="block pl-2 py-1.5 hover:bg-limestone-50 rounded text-sm text-text-secondary hover:text-text-primary truncate"
                        >
                          {section.title}
                        </a>
                      ))}
                    </div>
                  );
                })
              : localSections.map((section) => (
                  <a
                    key={section.localId}
                    href={section.id ? `#section-${section.id}` : undefined}
                    className="block py-1.5 hover:bg-limestone-50 rounded text-sm text-text-secondary hover:text-text-primary truncate"
                  >
                    {section.title}
                  </a>
                ))}
          </div>
        </div>
      </div>

      {!isReadOnly && subTab !== StructureSubTab.PROGRAMMING && (
        <TemplateSaveFooter
          isDirty={changed}
          isSaving={saveMutation.isPending}
          saveDisabled={hasBlankSectionTitles}
          onSave={() => {
            if (hasBlankSectionTitles) {
              const count = sectionsMissingTitle.length;
              notifyError(
                count === 1
                  ? "One section is missing a title. Sections require a title before saving."
                  : `${count} sections are missing a title. Sections require a title before saving.`,
              );
              return;
            }
            saveMutation.mutate(localSections);
          }}
          onDiscard={discardChanges}
          lastSavedAt={lastSavedAt}
        />
      )}
    </div>
  );
};
